import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { UserX, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

interface DisabledAccount {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  disabled_reason: string | null;
  updated_at: string;
}

const BLACKLIST_PREFIX = "Email domain blacklisted: ";

export const BlacklistedAccountsList = () => {
  const [accounts, setAccounts] = useState<DisabledAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("profiles")
      .select("id, email, first_name, last_name, disabled_reason, updated_at")
      .eq("is_disabled", true)
      .like("disabled_reason", `${BLACKLIST_PREFIX}%`)
      .order("updated_at", { ascending: false });
    if (error) toast.error("Failed to load disabled accounts");
    setAccounts((data ?? []) as DisabledAccount[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Refresh when blacklist changes
    const channel = supabase
      .channel("blacklist-disabled-accounts")
      .on("postgres_changes", { event: "*", schema: "public", table: "blacklisted_domains" }, () => load())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "profiles" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const extractDomain = (reason: string | null) =>
    reason?.startsWith(BLACKLIST_PREFIX) ? reason.slice(BLACKLIST_PREFIX.length) : "—";

  const filtered = accounts.filter((a) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      a.email.toLowerCase().includes(q) ||
      extractDomain(a.disabled_reason).toLowerCase().includes(q) ||
      `${a.first_name ?? ""} ${a.last_name ?? ""}`.toLowerCase().includes(q)
    );
  });

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserX className="h-5 w-5" /> Accounts Disabled by Blacklist
        </CardTitle>
        <CardDescription>
          Existing accounts automatically disabled because their email domain matches the Account Blacklist.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by email, name, or domain"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-9"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            {accounts.length === 0
              ? "No accounts have been disabled by the blacklist."
              : "No accounts match your filter."}
          </p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Blacklisted Domain</TableHead>
                  <TableHead>Disabled</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.email}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {[a.first_name, a.last_name].filter(Boolean).join(" ") || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="destructive">{extractDomain(a.disabled_reason)}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(a.updated_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
